// hemisphericRule — VFR (and stub IFR) cruising altitude hemispheric rule.
//
// Reference: 14 CFR 91.159 — VFR Cruising Altitudes and Flight Levels.
//
// > Except while holding in a holding pattern of 2 minutes or less,
// > or while turning, each person operating an aircraft under VFR in
// > level cruising flight more than 3,000 feet above the surface
// > shall maintain:
// >   (a) When operating below 18,000 feet MSL and on a magnetic course
// >       of zero degrees through 179 degrees, any ODD thousand foot
// >       MSL altitude + 500 feet (e.g. 1,500; 3,500; 5,500; etc.)
// >   (b) When operating below 18,000 feet MSL and on a magnetic course
// >       of 180 degrees through 359 degrees, any EVEN thousand foot
// >       MSL altitude + 500 feet (e.g. 2,500; 4,500; 6,500; etc.)
//
// Approximations in v1:
//   - "Above 3,000 ft AGL" is approximated as ≥3,000 ft MSL because
//     we don't have a terrain database at query time. Pilots over high
//     terrain (e.g. Rockies) should apply good judgement.
//   - IFR rule (odd/even thousands, no +500) is stubbed; pass
//     rule:'ifr' to opt in once wired.
//   - Magnetic variation in callers: use (trueCourse − 15 + 360) % 360
//     for CONUS West. A future WMM/IGRF lookup would improve this.

export type CruiseRule = 'vfr' | 'ifr';

export interface HemisphericInput {
  /** Magnetic course in degrees [0, 360). Values outside this range are
   *  normalised with modulo 360. */
  magneticCourseDeg: number;
  /** 'vfr' (odd/even thousands + 500) or 'ifr' (odd/even thousands).
   *  IFR is stubbed — treated as VFR for now. Default 'vfr'. */
  rule?: CruiseRule;
  /** Below this MSL altitude (ft) the rule does not apply. Default 3000. */
  minMslFt?: number;
}

// ─── Internal helpers ────────────────────────────────────────────────

function normaliseCourse(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** True → eastbound rule; false → westbound rule. */
function isEastbound(magneticCourseDeg: number): boolean {
  const c = normaliseCourse(magneticCourseDeg);
  return c >= 0 && c <= 179;
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Returns true if `altitudeFt` is compliant with the hemispheric rule
 * for the given input.
 *
 * Always returns true below `minMslFt` (rule does not apply).
 */
export function isHemisphericCompliant(
  altitudeFt: number,
  input: HemisphericInput,
): boolean {
  const minMsl = input.minMslFt ?? 3000;
  if (altitudeFt < minMsl) return true; // rule does not apply

  const rule = input.rule ?? 'vfr';
  const alt = Math.round(altitudeFt);

  if (rule === 'vfr') {
    // VFR — 14 CFR 91.159: odd+500 east, even+500 west, max 17,500.
    if (alt > 17500) return false; // above VFR cruising band
    if (isEastbound(input.magneticCourseDeg)) {
      // Pattern: alt % 2000 === 1500  (3500, 5500, 7500, 9500…)
      return alt % 2000 === 1500;
    } else {
      // Pattern: alt % 2000 === 500   (4500, 6500, 8500, 10500…)
      return alt % 2000 === 500;
    }
  }

  // IFR — 14 CFR 91.179.
  if (isEastbound(input.magneticCourseDeg)) {
    // Below 18,000: odd thousands (3000, 5000, 7000…).  alt % 2000 === 1000.
    // FL180+: odd flight levels (FL190, FL210…). 19000 % 2000 = 1000 ✓.
    return alt % 2000 === 1000;
  } else {
    // Below 18,000: even thousands (4000, 6000…).  alt % 2000 === 0.
    // FL180+: even flight levels (FL180, FL200…). 18000 % 2000 = 0 ✓.
    return alt % 2000 === 0;
  }
}

/**
 * Returns the smallest altitude (in feet) ≥ `altitudeFt` that is
 * compliant with the hemispheric rule. Walks in 100-ft increments,
 * which is fast enough for any reasonable input.
 *
 * If `altitudeFt` is below `minMslFt` the input is returned unchanged
 * (rule does not apply).
 */
export function nextCompliantAltitude(
  altitudeFt: number,
  input: HemisphericInput,
): number {
  const minMsl = input.minMslFt ?? 3000;
  if (altitudeFt < minMsl) return altitudeFt;

  let candidate = Math.round(altitudeFt);
  // Walk up in 100-ft steps until we hit a compliant altitude.
  // In practice this is at most 19 iterations (every 2000-ft band).
  for (let i = 0; i < 500; i++) {
    if (isHemisphericCompliant(candidate, input)) return candidate;
    candidate += 100;
  }
  // Fallback — should never reach here for sane inputs.
  return altitudeFt;
}

/**
 * Given an aircraft's `defaultCruiseFt` and a magnetic course, returns
 * the first compliant altitude ≥ `defaultCruiseFt`.
 */
export function defaultCompliantCruise(
  defaultCruiseFt: number,
  input: HemisphericInput,
): number {
  return nextCompliantAltitude(defaultCruiseFt, input);
}

/**
 * Human-readable label for the active rule (for tooltips / warnings).
 * Example: "VFR eastbound — odd thousands + 500 ft (14 CFR 91.159)"
 */
export function hemisphericRuleLabel(input: HemisphericInput): string {
  const minMsl = input.minMslFt ?? 3000;
  const rule = input.rule ?? 'vfr';
  const east = isEastbound(input.magneticCourseDeg);
  const direction = east ? 'eastbound' : 'westbound';
  const parity = east ? 'odd' : 'even';

  if (rule === 'ifr') {
    return `IFR ${direction} — ${parity} thousands / FLs (14 CFR 91.179)`;
  }
  return `VFR ${direction} — ${parity} thousands + 500 ft (14 CFR 91.159, above ${minMsl.toLocaleString()} ft MSL)`;
}

// ─── Altitude list ───────────────────────────────────────────────────

/**
 * Returns the complete ordered list of compliant cruising altitudes
 * (in feet MSL) for the given rule + magnetic course.
 *
 * VFR (default): 3,500–17,500 eastbound / 4,500–16,500 westbound (+500 ft).
 * IFR low:       3,000–17,000 eastbound / 4,000–16,000 westbound (even/odd).
 * IFR high:      FL190/FL210…FL410 eastbound / FL180/FL200…FL400 westbound.
 *
 * `opts.minFt` and `opts.maxFt` constrain the output list (useful for tests
 * or UI ranges). Defaults: minFt = 3,000; maxFt = 17,500 (VFR) / 41,000 (IFR).
 */
export function compliantAltitudes(
  input: HemisphericInput,
  opts?: { minFt?: number; maxFt?: number },
): number[] {
  const rule = input.rule ?? 'vfr';
  const east = isEastbound(input.magneticCourseDeg);
  const minFt = opts?.minFt ?? (input.minMslFt ?? 3000);
  const maxFt = opts?.maxFt ?? (rule === 'vfr' ? 17500 : 41000);

  const alts: number[] = [];

  if (rule === 'vfr') {
    // VFR: odd+500 east (3500, 5500…), even+500 west (4500, 6500…), cap 17,500.
    const start = east ? 3500 : 4500;
    for (let alt = start; alt <= Math.min(maxFt, 17500); alt += 2000) {
      if (alt >= minFt) alts.push(alt);
    }
    return alts;
  }

  // IFR — two bands.
  // Low band (below 18,000 ft):
  const lowStart = east ? 3000 : 4000;
  for (let alt = lowStart; alt <= Math.min(maxFt, 17000); alt += 2000) {
    if (alt >= minFt) alts.push(alt);
  }

  // High band (FL180+): odd FLs east (19000, 21000…), even FLs west (18000, 20000…).
  if (maxFt >= 18000) {
    const highStart = east ? 19000 : 18000;
    for (let alt = highStart; alt <= maxFt; alt += 2000) {
      if (alt >= minFt) alts.push(alt);
    }
  }

  return alts;
}

/**
 * Format an altitude in feet for display.
 *
 * - Below 18,000 ft: "7,500 ft"
 * - 18,000 ft and above: "FL180", "FL190", …
 */
export function formatAltitudeForDisplay(ft: number): string {
  if (ft >= 18000) {
    const fl = Math.round(ft / 100);
    return `FL${fl}`;
  }
  return `${ft.toLocaleString()} ft`;
}
